import { FC, useLayoutEffect, useRef, useState, useMemo, memo } from "react"
import classNames from "classnames"
import { useSelector, useDispatch } from "react-redux"
import Dropdown from "@/components/shared/Dropdown"
import { IconProps } from "@/components/shared/Icon"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import Tooltip from "@/components/shared/Tooltip"
import { RootState } from "@/store"
import { updateFacilityLevelSelectorsByIndex } from "@/store/facilitiesSlice"
import { FacilityLevelDropdown } from "@/types/facility"
import { Variant } from "@/types/index"
import { getErrorMessage, isShowingEllipsis } from "@/utils/index"
import "./index.scss"

const LevelFilters: FC = () => {
   const { levelTypes } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer.facilityDetails
   )
   const { facilityLevelSelectors, loadingFacility, errorFacility } =
      useSelector(({ facilitiesReducer }: RootState) => facilitiesReducer)
   const dispatch = useDispatch()

   const labelRefs = useRef<(HTMLSpanElement | null)[]>([])
   const [isTruncated, setIsTruncated] = useState<boolean[]>([])

   const levelSelectors: FacilityLevelDropdown[] = useMemo(() => {
      return facilityLevelSelectors.map((levelSelector) => {
         return {
            ...levelSelector,
            options: levelSelector.options.map((option) => ({
               ...option,
               selected: option.value === levelSelector.activeValue,
            })),
            disabled: levelSelector.options.length < 2,
            icon: levelTypes.find(
               (levelType) => levelType.id === levelSelector.typeId
            )?.icon as IconProps["name"],
         }
      })
   }, [facilityLevelSelectors, levelTypes])

   useLayoutEffect(() => {
      setIsTruncated(
         labelRefs.current.map(
            (labelSpan) => !!labelSpan && isShowingEllipsis(labelSpan)
         )
      )
   }, [levelSelectors])

   const handleLevelSelectionChange = (index: number, value: string) => {
      dispatch(updateFacilityLevelSelectorsByIndex({ index, value }))
   }
   return (
      <div
         className={classNames("level-filters", {
            "level-filters--loading": loadingFacility,
            "level-filters--error": errorFacility,
         })}
      >
         {loadingFacility ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : errorFacility ? (
            getErrorMessage(errorFacility)
         ) : (
            levelSelectors?.length > 0 &&
            levelSelectors.map((selector, index) => {
               const activeLabel =
                  selector.options.find((option) => option.selected)?.label ??
                  selector.label

               const dropdown = (
                  <div className="level-filters__selector">
                     <span
                        ref={(el) => {
                           labelRefs.current[index] = el
                        }}
                        className="level-filters__label"
                        aria-hidden
                     >
                        {activeLabel}
                     </span>
                     <Dropdown
                        key={`level-dropdown-${index}`}
                        size={Variant.LARGE}
                        iconName={selector.icon}
                        label={selector.label}
                        options={selector.options}
                        disabled={selector.disabled}
                        onSelectionChange={(selected) =>
                           handleLevelSelectionChange(index, selected[0])
                        }
                     />
                  </div>
               )

               return isTruncated[index] ? (
                  <Tooltip
                     key={`level-selector-tooltip-${index}`}
                     content={activeLabel}
                  >
                     {dropdown}
                  </Tooltip>
               ) : (
                  <div key={`level-selector-${index}`}>{dropdown}</div>
               )
            })
         )}
      </div>
   )
}

export default memo(LevelFilters)
